import { formatWhen } from "@/components/boo/access-copy";
import { CopyableValue } from "@/components/CopyableValue";
import { HostIcon } from "@/components/HostIcon";
import { Button } from "@/components/ui/button";
import { api, type ConnectorCloneToken, type ConnectorDetail } from "@/lib/api";
import { hostLabel } from "@/lib/code-host";
import { requestNoun } from "@/lib/connectors";
import { useMutation } from "@tanstack/react-query";
import { ExternalLink, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { CodeBrowser } from "./CodeBrowser";

// The code, read-only, then where it lives and how to work on it with your
// own tools. Boo-hosted: a personal token for this repository, shown once;
// pushes land on a review branch and come back here for approval. Code host:
// the repository itself.
export function CodePanel({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  return (
    <div className="flex flex-col gap-8">
      <CodeBrowser connector={connector} containerId={containerId} />
      <WhereItLives connector={connector} containerId={containerId} />
    </div>
  );
}

function WhereItLives({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  if (connector.source.kind !== "boo") {
    const kind = connector.source.kind;
    const host = hostLabel(kind);
    return (
      <div className="flex flex-col gap-3 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <HostIcon host={kind} className="size-4" />
          <p className="min-w-0 flex-1 text-sm font-medium">The code lives on {host}</p>
          {connector.source.repoUrl && (
            <Button asChild size="sm" variant="outline" className="gap-1.5">
              <a href={connector.source.repoUrl} target="_blank" rel="noreferrer">
                Open repository
                <ExternalLink className="size-3.5" />
              </a>
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground text-pretty">
          Clone it, change it and open a {requestNoun(kind)} as you normally would.{" "}
          {connector.name} runs what’s on the default branch: it goes live when someone merges the{" "}
          {requestNoun(kind)} and its checks pass. A change Boo’s own account puts on the default branch doesn’t go
          live by itself; when a later merge carries it live, it’s flagged.
        </p>
      </div>
    );
  }
  return <BooHostedCode connector={connector} containerId={containerId} />;
}

function BooHostedCode({ connector, containerId }: { connector: ConnectorDetail; containerId: string }) {
  const [token, setToken] = useState<(ConnectorCloneToken & { access: "clone" | "push" }) | null>(null);
  const mint = useMutation({
    mutationFn: (access: "clone" | "push") =>
      api.connectors.cloneToken(containerId, connector.slug, access).then((t) => ({ ...t, access })),
    onSuccess: setToken,
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not create a token"),
  });
  const authed = token ? withToken(token.url, token.token) : null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold">Work on it with your own tools</h2>
      <p className="text-sm text-muted-foreground text-pretty">
        Boo keeps this custom integration’s code. To work on it with your own editor or coding tools, create a personal
        token. A push goes to your own proposal branch and shows up here for approval. Nothing runs until a person approves it.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="gap-1.5" disabled={mint.isPending} onClick={() => mint.mutate("clone")}>
          {mint.isPending && mint.variables === "clone" ? <Loader2 className="size-3.5 animate-spin" /> : <KeyRound className="size-3.5" />}
          Create clone token
        </Button>
        {connector._permissions.canConfigure && (
          <Button size="sm" variant="outline" className="gap-1.5" disabled={mint.isPending} onClick={() => mint.mutate("push")}>
            {mint.isPending && mint.variables === "push" ? <Loader2 className="size-3.5 animate-spin" /> : <KeyRound className="size-3.5" />}
            Create push token
          </Button>
        )}
      </div>
      {token && authed && (
        <section className="ph-no-capture flex flex-col gap-3 rounded-lg border p-4">
          <p className="text-sm font-medium">
            Your {token.access === "push" ? "push" : "clone"} token. Copy it now: it isn’t shown again.
          </p>
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-muted-foreground">Clone</p>
            <CopyableValue value={`git clone ${authed}`} />
          </div>
          {token.access === "push" && (
            <div className="flex flex-col gap-1.5">
              <p className="text-xs text-muted-foreground">Push your changes for review</p>
              <CopyableValue value={`git push origin HEAD:${pushRef(token.branch)}`} />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <p className="text-xs text-muted-foreground">Token</p>
            <CopyableValue value={token.token} />
          </div>
          <p className="text-xs text-muted-foreground">Expires {formatWhen(token.expiresAt)}.</p>
        </section>
      )}
    </section>
  );
}

// The backend's url already carries the token (https://boo:<token>@host/…);
// a bare url gets it added the same way.
function withToken(url: string, token: string): string {
  try {
    const u = new URL(url);
    if (u.password) return url;
    u.username = "boo";
    u.password = token;
    return u.toString();
  } catch {
    return url;
  }
}

// Pushes to main are refused; a push goes to the person's proposal branch.
function pushRef(branch: string): string {
  const name = branch.replace(/^refs\/heads\//, "");
  return `refs/heads/${name.startsWith("proposals/") ? name : `proposals/${name}`}`;
}
